import { Box, Flex, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import {
  FaBolt,
  FaBrain,
  FaCloud,
  FaCode,
  FaDatabase,
  FaMobileAlt,
  FaRocket,
  FaSearch,
  FaServer,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";

const MotionBox = motion.create(Box);

const leftSignals = [
  { label: "Agents", icon: FaBrain, color: "#00FFD0", path: "/aiagents" },
  { label: "Build", icon: FaCode, color: "#FF6F20", path: "/webapps" },
  { label: "Scrapers", icon: FaSearch, color: "#5BC0DE", path: "/webscrapers" },
  { label: "Consult", icon: FaRocket, color: "#D9534F", path: "/consultations" },
  { label: "CRM", icon: FaUsers, color: "#9B59B6", path: "/crmplatforms" },
  { label: "Mobile", icon: FaMobileAlt, color: "#008BFF", path: "/mobileapps" },
  { label: "Secure", icon: FaShieldAlt, color: "#8B5CF6", path: "/apigateways" },
];

const rightSignals = [
  { label: "AI", icon: FaBolt, color: "#00FFD0", path: "/aiagents" },
  { label: "MCP", icon: FaServer, color: "#8B5CF6", path: "/aiagents" },
  { label: "RAG", icon: FaDatabase, color: "#FFB400", path: "/aiagents" },
  { label: "Cloud", icon: FaCloud, color: "#38BDF8", path: "/apigateways" },
  { label: "Scale", icon: FaRocket, color: "#FF6F20", path: "/services" },
];

export default function SiteAtmosphere() {
  return (
    <Box
      pointerEvents="none"
      position="fixed"
      inset={0}
      zIndex={2}
      display={{ base: "none", xl: "block" }}
      sx={{
        "@media (max-width: 1699px)": {
          display: "none",
        },
      }}
    >
      <Rail side="left">
        <VStack align="stretch" spacing={3}>
          {leftSignals.map((item, index) => (
            <SignalCard key={item.label} item={item} index={index} align="left" />
          ))}
        </VStack>
      </Rail>

      <Rail side="right">
        <VStack align="stretch" spacing={4}>
          {rightSignals.map((item, index) => (
            <SignalCard key={item.label} item={item} index={index} align="right" />
          ))}
        </VStack>
      </Rail>
    </Box>
  );
}

function Rail({ side, children }) {
  const isLeft = side === "left";

  return (
    <MotionBox
      position="absolute"
      top="112px"
      bottom="72px"
      w="148px"
      left={isLeft ? "max(20px, calc((100vw - 1660px) / 2))" : "auto"}
      right={!isLeft ? "max(20px, calc((100vw - 1660px) / 2))" : "auto"}
      initial={{ opacity: 0, x: isLeft ? -20 : 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
    >
      <Box
        position="absolute"
        top={0}
        bottom={0}
        left={isLeft ? 4 : "auto"}
        right={!isLeft ? 4 : "auto"}
        w="1px"
        bg="linear-gradient(180deg, transparent, rgba(0, 255, 208, 0.36), rgba(255, 111, 32, 0.32), transparent)"
      />
      <Box
        position="absolute"
        top="16%"
        left={isLeft ? 0 : "auto"}
        right={!isLeft ? 0 : "auto"}
        w="92px"
        h="92px"
        borderRadius="full"
        border="1px solid rgba(0, 255, 208, 0.18)"
        boxShadow="0 0 46px rgba(0, 255, 208, 0.12)"
      />
      <Box
        position="absolute"
        bottom="11%"
        left={isLeft ? "34px" : "auto"}
        right={!isLeft ? "34px" : "auto"}
        w="58px"
        h="58px"
        borderRadius="full"
        border="1px solid rgba(255, 111, 32, 0.24)"
        boxShadow="0 0 38px rgba(255, 111, 32, 0.14)"
      />
      <Flex
        minH="100%"
        align="center"
        justify={isLeft ? "flex-start" : "flex-end"}
        position="relative"
      >
        {children}
      </Flex>
    </MotionBox>
  );
}

function SignalCard({ item, index, align }) {
  const isLeft = align === "left";

  return (
    <MotionBox
      as={RouterLink}
      to={item.path}
      aria-label={`Explore ${item.label} services`}
      bg="rgba(8, 13, 24, 0.62)"
      border="1px solid rgba(255, 255, 255, 0.12)"
      borderRadius="lg"
      boxShadow="0 16px 38px rgba(0, 0, 0, 0.22)"
      p={3}
      w="128px"
      pointerEvents="auto"
      textDecoration="none"
      sx={{
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
      _hover={{
        borderColor: `${item.color}88`,
        boxShadow: `0 18px 42px ${item.color}22`,
        textDecoration: "none",
        transform: "translateY(-2px)",
      }}
      _focusVisible={{
        outline: "none",
        boxShadow: `0 0 0 3px ${item.color}66`,
      }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 + index * 0.1, duration: 0.45 }}
    >
      <HStack spacing={3} justify={isLeft ? "flex-start" : "flex-end"}>
        {isLeft && <SignalIcon item={item} />}
        <Text color="#F4F4F6" fontSize="sm" fontWeight="bold">
          {item.label}
        </Text>
        {!isLeft && <SignalIcon item={item} />}
      </HStack>
    </MotionBox>
  );
}

function SignalIcon({ item }) {
  return (
    <Flex
      align="center"
      justify="center"
      w={8}
      h={8}
      borderRadius="lg"
      bg={`${item.color}1F`}
      border="1px solid"
      borderColor={`${item.color}55`}
      flexShrink={0}
    >
      <Icon as={item.icon} color={item.color} boxSize={4} />
    </Flex>
  );
}
